import React, {useState} from 'react'
import {useFormContext} from "react-hook-form";
import UiInputText from "./UiInputText";
import UiInputToggle from "./UiInputToggle";
import UiInputDate from "./UiInputDate";
import UiInputList from "./UiInputList";
import UiInputSuggest from "./UiInputSuggest";
import UiInputLocalizacao from "./UiInputLocalizacao";
import CepAutocompleteWrapper from "./CepAutocompleteWrapper";

export const TIPO_PESSOA_FISICA = 'fisica';
export const TIPO_PESSOA_JURIDICA = 'juridica';

const tipoPessoaList = [
    { id: TIPO_PESSOA_FISICA, title: 'Física' },
    { id: TIPO_PESSOA_JURIDICA, title: 'Jurídica' },
];

const generoList = [
    { id: 1, title: 'Masculino' },
    { id: 2, title: 'Feminino' },
];

export const ehPessoaJuridica = (tipo: any) => {
    const valor = String(tipo ?? '').trim().toLowerCase();
    return valor === TIPO_PESSOA_JURIDICA || valor === 'j' || valor === 'pj' || valor === '2';
};

// convertModelToFormData descarta valor falsy, então checkbox desmarcado não chega
// ao servidor sem isto.
export const booleanParaEnvio = (valor: any) => (valor ? '1' : '0');

export const pessoaParaEnvio = (pessoa: any) => ({
    ...pessoa,
    ativo: booleanParaEnvio(pessoa?.ativo),
});

export const pessoaVazia = () => ({
    tipo: TIPO_PESSOA_JURIDICA,
    ativo: true,
    endereco: {},
});

interface PessoaFieldsProps {
    /** Vazio monta os campos no nível raiz; 'pessoa' monta `pessoa.campo`. */
    prefixo?: string;
}

export default function PessoaFields({ prefixo = '' }: PessoaFieldsProps) {
    const { watch } = useFormContext();
    const campo = (nome: string) => (prefixo ? `${prefixo}.${nome}` : nome);
    const [cepLoading, setCepLoading] = useState(false);

    const juridica = ehPessoaJuridica(watch(campo('tipo')));

    return (
        <>
            <CepAutocompleteWrapper
                cepField={campo('endereco.cep')}
                enderecoField={campo('endereco.logradouro')}
                bairroField={campo('endereco.bairro')}
                cidadeField={campo('endereco.cidade')}
                numeroField={campo('endereco.numero')}
                localizacaoField={campo('endereco.localizacao')}
                onLoadingChange={setCepLoading}
            />

            <fieldset>
                <legend>Tipo</legend>
                <div className="row">
                    <UiInputList size={3} name={campo('tipo')} list={tipoPessoaList} idField="id"
                                 titleField="title" label="Tipo de pessoa"/>
                    <UiInputToggle size={2} name={campo('ativo')} label="Ativa"/>
                    <UiInputText size={7} name={campo('identificador')} label="Identificador / Apelido / Outros Nomes"/>
                </div>
            </fieldset>

            { juridica &&
                <fieldset>
                    <legend>Pessoa Jurídica</legend>
                    <div className="row">
                        <UiInputText mask="uppercase" size={6} name={campo('razaoSocial')} label="Razão Social"/>
                        <UiInputText mask="uppercase" size={6} name={campo('nomeFantasia')} label="Nome Fantasia"/>
                        <UiInputText mask="cnpj" maxLength={18} size={4} name={campo('cnpj')} label="CNPJ"/>
                        <UiInputText size={4} name={campo('inscricaoEstadual')} label="Insc. Estadual"/>
                        <UiInputText size={4} name={campo('inscricaoMunicipal')} label="Insc. Municipal"/>
                    </div>
                </fieldset>
            }

            <fieldset>
                <legend>Pessoa Física{juridica ? ' (responsável pela empresa)' : ''}</legend>
                <div className="row">
                    <UiInputText mask="uppercase" size={6} name={campo('nome')} label="Nome completo"/>
                    <UiInputText mask="cpf" maxLength={14} size={3} name={campo('cpf')} label="CPF"/>
                    <UiInputDate size={3} name={campo('dataNascimento')} label="Data de Nascimento"/>
                    <UiInputText size={2} name={campo('rg')} label="RG"/>
                    <UiInputText mask="uppercase" size={3} name={campo('rgOrgaoExpedidor')} label="Órgão Expedidor"/>
                    <UiInputList size={3} name={campo('genero')} list={generoList} idField="id"
                                 titleField="title" label="Gênero"/>
                    <UiInputText mask="uppercase" size={4} name={campo('estadoCivil')} label="Estado Civil"/>
                    <UiInputText mask="uppercase" size={3} name={campo('profissao')} label="Profissão"/>
                    <UiInputText mask="phone" maxLength={15} size={3} name={campo('telefone')} label="Telefone"/>
                    <UiInputText mask="lowercase" size={4} name={campo('email')} label="E-mail"/>
                    <UiInputText mask="uppercase" size={2} name={campo('contato')} label="Contato"/>
                </div>
            </fieldset>

            <fieldset>
                <legend>Endereço</legend>
                <div className="row">
                    <UiInputText mask="cep" size={2} name={campo('endereco.cep')} label="CEP" loading={cepLoading}/>
                    <UiInputText mask="uppercase" size={5} name={campo('endereco.logradouro')} label="Endereço"/>
                    <UiInputText size={2} name={campo('endereco.numero')} label="Número"
                                 showDisableCheckbox={true} onDisableSetValue={`S/N`} disableButtonLabel={`S/N`}/>
                    <UiInputText mask="uppercase" size={3} name={campo('endereco.complemento')} label="Complemento"/>
                    <UiInputText mask="uppercase" size={3} name={campo('endereco.bairro')} label="Bairro"/>
                    <UiInputSuggest size={5} router="cidade" idField="id" titleField="nome"
                                    name={campo('endereco.cidade')} label="Cidade"/>
                    <UiInputLocalizacao size={4} name={campo('endereco.localizacao')} label="Localização"
                                        camposEndereco={{
                                            logradouro: campo('endereco.logradouro'),
                                            numero: campo('endereco.numero'),
                                            bairro: campo('endereco.bairro'),
                                            cidade: campo('endereco.cidade'),
                                        }}/>
                </div>
            </fieldset>
        </>
    )
}
